import { useTranslation } from "react-i18next";
import type { ProductUsageRow } from "@/lib/types";
import type { ProductUsageSegment, ProductUsageWindow } from "./product-usage-matrix";

export function ProductUsageSummary({
  products,
  segment,
  window,
  hasMore,
}: {
  products: ProductUsageRow[];
  segment: ProductUsageSegment;
  window: ProductUsageWindow;
  hasMore: boolean;
}) {
  const { t, i18n } = useTranslation();
  const totals = products.reduce((sum, product) => ({
    licenses: sum.licenses + BigInt(product.effective_active_licenses),
    activations: sum.activations + BigInt(product.lifecycle_active_activations),
    seen: sum.seen + BigInt(product.recently_seen_devices),
  }), { licenses: 0n, activations: 0n, seen: 0n });
  const licensesAvailable = segment !== "combined" && products.every((product) => product.metric_availability.effective_active_licenses);
  const metrics = [
    { key: "licenses", label: t("usage.columns.licenses", "Effective Active Licenses"), value: licensesAvailable ? totals.licenses : null },
    { key: "activations", label: t("usage.columns.activations", "Lifecycle-active Activations"), value: totals.activations },
    { key: "seen", label: `${t("usage.columns.seen", "Recently Seen Devices")} · ${t(`usage.windows.${window}`, window)}`, value: totals.seen },
  ];

  return (
    <section className="shrink-0 border-b border-line pb-4" aria-label={t("usage.summaryAria", "Product usage totals")} aria-describedby="usage-summary-scope">
      <dl className="grid grid-cols-3 divide-x divide-line py-2 sm:py-3">
        {metrics.map(({ key, label, value }) => (
          <div key={key} className="flex min-w-0 flex-col gap-2 px-3 first:pl-0 last:pr-0 sm:px-6">
            <dt className="text-[11px] leading-4 text-muted sm:text-xs">{label}</dt>
            <dd data-usage-total={key} className={value === null
              ? "order-first flex min-h-10 items-center wrap-break-word text-sm text-muted sm:min-h-12 sm:text-base"
              : "order-first break-all font-mono text-[28px] leading-10 tabular-nums tracking-[-0.04em] text-ink sm:text-[40px] sm:leading-[48px]"}>
              {value === null ? t("usage.unavailable", "Unavailable") : value.toLocaleString(i18n?.resolvedLanguage ?? "en")}
            </dd>
          </div>
        ))}
      </dl>
      <p id="usage-summary-scope" className="pt-2 text-xs text-muted">
        {hasMore
          ? t("usage.summaryPartial", { count: products.length, defaultValue: `Totals for ${products.length} loaded Products only. Load more to include the remaining Products.` })
          : t("usage.summaryComplete", { count: products.length, defaultValue: `All ${products.length} Products included in totals.` })}
      </p>
    </section>
  );
}
